import { makeAutoObservable } from 'mobx';
import { IDType } from '../../models';
import type { RootStoreClass } from '../rootStore';
import StageClass, { SCHEMA } from './Classes/Stage.class';
import TournamentTableStoreApi from './tournament.api';

const DEFAULT_VALUES = {
    participants: null,
    stages: null
};

export default class TournamentTableStore {
    participantsLoading = true;

    rootStore: RootStoreClass;

    tournamentId: IDType;

    api: TournamentTableStoreApi;

    stages: StageClass[] = [];

    participants: { team: string; name: string }[] = [];

    constructor(rootStore: RootStoreClass, tournamentId: IDType) {
        this.rootStore = rootStore;
        this.tournamentId = tournamentId;

        Object.entries(DEFAULT_VALUES).forEach(([key, value]) => this.updateField(key, value));

        this.api = new TournamentTableStoreApi({
            rootStore,
            tournamentId
        });

        this.initialLoad();

        makeAutoObservable(this);
    }

    get stagesList() {
        if (!this.stages) {
            return [];
        }
        return this.stages.filter((stage) => stage.id);
    }

    loadParticipants = async () => {
        this.participantsLoading = true;

        const participants = await this.api.getTournamentPlayers();
        this.participants = participants.filter(
            (participant: { team: string }) => participant.team === 'main'
        );

        this.participantsLoading = false;
        return this.participants;
    };

    loadStages = async () => {
        const stagesData = await this.api.getTournamentStages();
        this.stages = stagesData.map(
            (data: Partial<SCHEMA>) => new StageClass(this.api, data, this)
        );
        return this.stages;
    };

    deleteStage = (id: IDType) => {
        this.stages = [...this.stages.filter((stage) => stage.id !== id)];
    };

    addStage = (stage: StageClass) => {
        this.stages = [...this.stages, stage].sort((a, b) => (a.id || 0) - (b.id || 0));
    };

    newStage = (stageData: Partial<SCHEMA> = {}) => {
        const newStage = new StageClass(this.api, stageData, this);
        this.addStage(newStage);
        return newStage;
    };

    initialLoad = async () => {
        await this.loadParticipants();
        this.loadStages();
    };

    clearStore = () => {
        Object.entries(DEFAULT_VALUES).forEach(([key, value]) => {
            this.updateField(key, value);
        });
    };

    [key: string]: any;

    updateField = (field: string, value: any) => {
        this[field] = value;
    };
}
